import { ActionModal, FormError } from '@kcftech/design-system';
import {
    ContentType,
    FilesUpdateRequest,
    IPictureFileMetadataResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { TextInput } from 'components/forms/TextInput';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { excludeSpecialChars } from 'pages/Settings/FileLibrary/shared';
import { RegisterOptions, useForm } from 'react-hook-form';
import { queryClient } from 'utils/query';

const nameRules: RegisterOptions = {
    required: 'Please enter a folder name.',
    maxLength: { value: 50, message: 'Folder name cannot exceed 50 characters.' },
    pattern: {
        value: excludeSpecialChars,
        message:
            'Folder name cannot contain any of the following characters: < > : “ / \\ | ? *',
    },
};

type FolderNameForm = {
    name: string;
};

export type FolderModalProps = Readonly<{
    folder?: IPictureFileMetadataResponse;
    parentFolderId?: string;
    locationId?: string;
    corporationId?: string;
    /**
     * Callback for when edit was successful.
     */
    onSave: () => void;
    /**
     * Callback for when edit was cancelled.
     */
    onCancel: () => void;
}>;

export function FolderModal({
    folder,
    parentFolderId,
    corporationId,
    locationId,
    onSave,
    onCancel,
}: FolderModalProps) {
    const { showToast } = useNotificationContext();

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitted, isValid, isValidating, isSubmitting },
    } = useForm<FolderNameForm>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            name: folder?.DisplayName,
        },
    });

    const { mutate: saveFolderName, isPending: isSavingEdits, isError } = useMutation({
        mutationFn: async (data: FolderNameForm) => {
            if (folder) {
                await sdClient.updateFile({
                    fileId: folder.Id,
                    body: new FilesUpdateRequest({
                        DisplayName: data.name,
                        ParentFolderId: folder.ParentFolderId ?? '',
                        Description: folder.Description,
                        GroupIds: folder.GroupIds,
                    }),
                });
            }
        },

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['file-library-table'],
            });
            onSave();
            showToast({ type: 'success', content: 'Successfully updated folder' });
        },

        onError: () => {
            showToast({ type: 'error', content: 'Failed to update folder' });
        },
    });

    const { mutate: addFolder, isPending: isSaving } = useMutation({
        mutationFn: async (data: FolderNameForm) => {
            await sdClient.createFile({
                fileType: ContentType.Folder,
                displayName: data.name,
                parentFolderId,
                locationId,
                corporationId,
            });
        },

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['file-library-table'],
            });
            onSave();
            showToast({ type: 'success', content: 'Successfully added folder' });
        },

        onError: () => {
            showToast({ type: 'error', content: 'Failed to add folder' });
        },
    });

    return (
        <ActionModal
            data-testid="folder-modal"
            height={315}
            width={folder !== undefined && folder.DisplayName.length > 45 ? 620 : 580}
            title={folder ? `Edit ${folder.DisplayName}` : 'Add New Folder'}
            actionButtonText={folder ? 'Save' : 'Add'}
            actionable={
                (!isSubmitted || isValid) &&
                !(isValidating || isSubmitting) &&
                (!isSaving || !isSavingEdits)
            }
            closeButtonText="Cancel"
            onClose={onCancel}
            onAction={
                folder
                    ? handleSubmit((data) => saveFolderName(data))
                    : handleSubmit((formData) => addFolder(formData))
            }
        >
            <ValidatableLabel label="Folder Name" required error={errors.name}>
                <TextInput
                    required
                    placeholder="Enter a folder name"
                    invalid={errors.name !== undefined}
                    {...register('name', nameRules)}
                />
            </ValidatableLabel>
            <FormError
                message={isError ? `Unable to edit ${folder?.DisplayName}` : undefined}
            />
        </ActionModal>
    );
}
